import React, { useEffect, useState } from 'react';
import { formatSparks } from '../../api/sparksApi';
import useCountUp from '../../hooks/useCountUp';
import AddyAvatar from './AddyAvatar';
import './sparks.css';

const ROW_H = 56;
const MAX_ROWS = 4;
export const MOVE_MS = 2400; // matches the .sparks-climb__row and .sparks-road transitions in sparks.css
const reducedMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Which end-board rows the climb shows and where each one starts. `rows` carry absolute `rank` (top 10 or
 * neighbourhood). The child's row comes up from its old place (or from below the list when off-screen / new) and
 * the children it passed each start one place higher, so they slide down as it goes by. Slots count from the top
 * of the window; MAX_ROWS means below it.
 */
export function climbRows({ kind, from, to, rows }) {
  const byRank = new Map((rows || []).map((r, i) => [r.rank ?? i + 1, { ...r, rank: r.rank ?? i + 1 }]));
  const window = [];
  for (let rank = to; rank < to + MAX_ROWS; rank += 1) {
    const row = byRank.get(rank);
    if (row) window.push(row);
  }
  const youStart = kind === 'climb' && from - to < MAX_ROWS ? from - to : MAX_ROWS;
  return window.map((row, i) => ({
    ...row,
    endSlot: i,
    startSlot: row.isYou ? youStart : i <= youStart ? i - 1 : i,
  }));
}

/**
 * The recap's Sparks Stars moment (recapTally.boardClimb): a mini board where the child's row slides up past the
 * children they overtook in the top 10 or their neighbourhood, or a progress bar when they got closer without
 * changing place. Plays once on mount; reduced motion shows the end state.
 */
export default function SparksStarsClimb({ climb, catalogue }) {
  const [moved, setMoved] = useState(false);
  useEffect(() => {
    // Two frames so the start positions paint before the transition to the end ones.
    let second;
    const first = requestAnimationFrame(() => { second = requestAnimationFrame(() => setMoved(true)); });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, []);
  // A climb ticks the child's number down from the old place; a child new to the board was never shown one.
  const yourRank = useCountUp(climb.kind === 'climb' && !moved ? climb.from : climb.to ?? 0, reducedMotion() ? 0 : MOVE_MS);

  if (climb.kind === 'closer') {
    return (
      <div className="mt-3 rounded-xl bg-[#F1EEF7] px-3 py-2 text-left" data-testid="sparks-stars-closer">
        <div className="text-sm font-semibold text-[#5C3BD1]">⭐ {climb.text}</div>
        <div className="mt-1 text-xs text-gray-600">{climb.label || 'Road to the top 10'}</div>
        <div className="sparks-road mt-1">
          <span style={{ width: `${Math.round((moved ? climb.toShare : climb.fromShare) * 100)}%` }} />
        </div>
        <div className="mt-1 text-xs text-gray-600">{formatSparks(climb.gap)} more Sparks to go</div>
      </div>
    );
  }

  const rows = climbRows(climb);
  return (
    <div className="mt-3 rounded-xl bg-[#F1EEF7] px-3 py-2" data-testid="sparks-stars-climb">
      <div className="text-sm font-semibold text-[#5C3BD1]">⭐ {climb.text}</div>
      <div className="sparks-climb mt-2" style={{ height: rows.length * ROW_H }}>
        {rows.map((row) => {
          const slot = moved ? row.endSlot : row.startSlot;
          return (
            <div
              key={`${row.rank}-${row.firstName}-${row.isYou ? 'you' : ''}`}
              className={`sparks-climb__row${row.isYou ? ' sparks-climb__row--you' : ''}`}
              style={{ transform: `translateY(${slot * ROW_H}px)`, opacity: slot >= MAX_ROWS || slot < 0 ? 0 : 1 }}
            >
              <span className="sparks-climb__rank">{row.isYou ? yourRank : row.rank}</span>
              <span className="sparks-climb__addy">
                <AddyAvatar equipped={row.equipped} catalogue={catalogue} size={52} width={60} animate={false} />
              </span>
              <span className="sparks-climb__name">{row.isYou ? 'You' : row.firstName}</span>
              <span className="sparks-climb__sparks">{formatSparks(row.sparks)} ✨</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
